export type AnalyticsRangePreset = '2h' | '24h' | '7d' | '30d' | '90d' | '365d' | 'custom';
export type AnalyticsRollingRangePreset = '2h' | '24h';

export interface AnalyticsFilterScope {
  preset: AnalyticsRangePreset;
  from: string;
  to: string;
  platforms: number[];
  appVersions: string[];
  countries: string[];
}

export function readInitialAnalyticsFilterScope(): AnalyticsFilterScope {
  const query = new URLSearchParams(window.location.search);
  const preset = parseAnalyticsRangePreset(query.get('analyticsRange'));
  const defaultRange = createPresetRange(readPresetDays(preset));
  return {
    preset,
    from: query.get('analyticsFrom') ?? defaultRange.from,
    to: query.get('analyticsTo') ?? defaultRange.to,
    platforms: (query.get('analyticsPlatforms') ?? '')
      .split(',')
      .filter(hasText)
      .map(Number)
      .filter(isPlatformCode),
    appVersions: uniqueStrings(query.getAll('analyticsVersion').filter(isAppVersion)),
    countries: uniqueStrings(query.getAll('analyticsCountry').filter(isCountryCode)),
  };
}

export function writeAnalyticsFilterScopeToLocation(scope: AnalyticsFilterScope): void {
  const url = new URL(window.location.href);
  url.searchParams.set('analyticsRange', scope.preset);
  url.searchParams.set('analyticsFrom', scope.from);
  url.searchParams.set('analyticsTo', scope.to);
  setOptionalParameter(
    url.searchParams,
    'analyticsPlatforms',
    scope.platforms.length === 0 ? null : scope.platforms.join(','),
  );
  setRepeatedParameters(url.searchParams, 'analyticsVersion', scope.appVersions);
  setRepeatedParameters(url.searchParams, 'analyticsCountry', scope.countries);
  window.history.replaceState({}, '', url);
}

export function createAnalyticsFilterQuery(
  environmentId: string,
  scope: AnalyticsFilterScope,
): URLSearchParams {
  const range = createQueryRange(scope);
  const query = new URLSearchParams({ environmentId, ...range });
  if (scope.platforms.length > 0) {
    query.set('platformCodes', scope.platforms.join(','));
  }
  for (const appVersion of scope.appVersions) {
    query.append('appVersion', appVersion);
  }
  for (const countryCode of scope.countries) {
    query.append('country', countryCode);
  }
  return query;
}

export function createPresetAnalyticsFilterScope(
  current: AnalyticsFilterScope,
  preset: AnalyticsRangePreset,
): AnalyticsFilterScope {
  if (preset === 'custom' || isRollingRangePreset(preset)) {
    return { ...current, preset };
  }
  return { ...current, preset, ...createPresetRange(readPresetDays(preset)) };
}

export function isRollingRangePreset(
  value: AnalyticsRangePreset,
): value is AnalyticsRollingRangePreset {
  return value === '2h' || value === '24h';
}

function createQueryRange(scope: AnalyticsFilterScope): { from: string; to: string } {
  if (!isRollingRangePreset(scope.preset)) {
    return { from: `${scope.from}T00:00:00.000Z`, to: nextDayIso(scope.to) };
  }
  const to = new Date();
  const from = new Date(to.getTime() - readPresetHours(scope.preset) * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function createPresetRange(days: number): { from: string; to: string } {
  const today = new Date();
  const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - days + 1);
  return { from: dateInputValue(start), to: dateInputValue(end) };
}

function nextDayIso(value: string): string {
  const day = new Date(`${value}T00:00:00.000Z`);
  day.setUTCDate(day.getUTCDate() + 1);
  return day.toISOString();
}

function parseAnalyticsRangePreset(value: string | null): AnalyticsRangePreset {
  return value === '2h'
    || value === '24h'
    || value === '7d'
    || value === '30d'
    || value === '90d'
    || value === '365d'
    || value === 'custom'
    ? value
    : '30d';
}

function readPresetHours(value: AnalyticsRollingRangePreset): number {
  return value === '2h' ? 2 : 24;
}

function readPresetDays(value: AnalyticsRangePreset): number {
  if (value === '7d') return 7;
  if (value === '90d') return 90;
  if (value === '365d') return 365;
  if (value === '2h' || value === '24h') return 1;
  return 30;
}

function setOptionalParameter(
  query: URLSearchParams,
  key: string,
  value: string | null,
): void {
  if (value === null) query.delete(key);
  else query.set(key, value);
}

function setRepeatedParameters(
  query: URLSearchParams,
  key: string,
  values: readonly string[],
): void {
  query.delete(key);
  for (const value of values) query.append(key, value);
}

function hasText(value: string): boolean {
  return value !== '';
}

function isPlatformCode(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 65535;
}

function isAppVersion(value: string): boolean {
  return /^[\x20-\x7e]{1,64}$/.test(value);
}

function isCountryCode(value: string): boolean {
  return /^[A-Z]{2}$/.test(value);
}

function uniqueStrings(values: string[]): string[] {
  return [...new Set(values)];
}

function dateInputValue(value: Date): string {
  return value.toISOString().slice(0, 10);
}
